import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  cleanEmail,
  getEmailSuggestion,
  hasSuspiciousEmailLocalPart,
  isValidEmail,
} from "../../../../util/validation";
import { isDisposableEmail } from "../../../../util/personalDataRules";

export interface EmailFieldFeedback {
  /** Hard message shown in error style (structural problem or likely typo). */
  error: string | null;
  /** Soft message shown in muted/warning style (suspicious but not blocking). */
  warning: string | null;
}

/**
 * Derives the inline message to show next to an email field, based on the raw
 * value the user has typed. Design-system agnostic: returns ready-to-render
 * translated strings so both the MUI and ShadCN inputs can reuse it.
 *
 * Priority: typo suggestion > structural error > disposable-domain error >
 * suspicious warning. The typo
 * suggestion wins over the generic structural error because some typos (e.g.
 * "gmx.d") are structurally invalid yet have a clear fix to offer.
 *
 * Nothing is shown until the field has been touched (blurred at least once).
 */
export const useEmailFieldFeedback = (
  rawValue: string,
  touched: boolean
): EmailFieldFeedback => {
  const { t } = useTranslation();

  return useMemo(() => {
    const empty: EmailFieldFeedback = { error: null, warning: null };
    if (!touched) return empty;

    const cleaned = cleanEmail(rawValue);
    if (cleaned === "") return empty;

    const suggestion = getEmailSuggestion(cleaned);
    if (suggestion) {
      return {
        error: t("book_location.personal_data.validation.email_typo_suggestion", {
          suggestion,
        }),
        warning: null,
      };
    }

    if (!isValidEmail(cleaned)) {
      return {
        error: t("book_location.personal_data.validation.email_invalid"),
        warning: null,
      };
    }

    // Disposable domains are structurally valid but blocked by
    // `isEmailAcceptable` (rigorous personal-data rules), so show a hard
    // error explaining why the step won't proceed.
    if (isDisposableEmail(cleaned)) {
      return {
        error: t("book_location.personal_data.validation.email_disposable"),
        warning: null,
      };
    }

    if (hasSuspiciousEmailLocalPart(cleaned)) {
      return {
        error: null,
        warning: t("book_location.personal_data.validation.email_suspicious"),
      };
    }

    return empty;
  }, [rawValue, touched, t]);
};
